import type { AgGrid } from "@jsq/ds/core";
import type { WorkflowQueryFilters } from "../types";

type SetFilterModel = { filterType: "set"; values: string[] | null };
type DateFilterModel = {
  filterType: "date";
  type:
    | "equals"
    | "greaterThan"
    | "greaterThanOrEqual"
    | "lessThan"
    | "notEqual"
    | "inRange"
    | "blank"
    | "notBlank";
  dateFrom: string | null;
  dateTo: string | null;
};

/** Strip the time portion AG Grid appends ("2024-01-01 00:00:00" → "2024-01-01"). */
const toDateOnly = (dateStr: string): string => dateStr.split(" ")[0];

/**
 * Converts an AG Grid FilterModel into WorkflowQueryFilters for GraphQL.
 *
 * Column → GQL variable mapping:
 *   status       (set)  → statuses
 *   workflowType (set)  → workflowTypes
 *   clientName   (set)  → clientIds   (filter stores client IDs as values)
 *   assignee     (set)  → assignedToUserIds (filter stores user IDs as values)
 *   dueDate      (date) → dueDateBefore / dueDateAfter
 */
export function getWorkflowFilterVars(
  filterModel: AgGrid.FilterModel | null | undefined,
): WorkflowQueryFilters {
  if (!filterModel || Object.keys(filterModel).length === 0) {
    return {};
  }

  const result: WorkflowQueryFilters = {};

  // --- Set filters ---
  const statusFilter = filterModel["status"] as SetFilterModel | undefined;
  if (statusFilter?.filterType === "set" && Array.isArray(statusFilter.values)) {
    const vals = statusFilter.values.filter(Boolean);
    if (vals.length > 0) result.statuses = vals;
  }

  const typeFilter = filterModel["workflowType"] as SetFilterModel | undefined;
  if (typeFilter?.filterType === "set" && Array.isArray(typeFilter.values)) {
    const vals = typeFilter.values.filter(Boolean);
    if (vals.length > 0) result.workflowTypes = vals;
  }

  // clientName column stores the clientId as the filter value (via filterParams.values)
  const clientFilter = filterModel["clientName"] as SetFilterModel | undefined;
  if (clientFilter?.filterType === "set" && Array.isArray(clientFilter.values)) {
    const vals = clientFilter.values.filter(Boolean);
    if (vals.length > 0) result.clientIds = vals;
  }

  // assignee column stores the userId as the filter value (via filterParams.values)
  const assigneeFilter = filterModel["assignee"] as SetFilterModel | undefined;
  if (assigneeFilter?.filterType === "set" && Array.isArray(assigneeFilter.values)) {
    const vals = assigneeFilter.values.filter(Boolean);
    if (vals.length > 0) result.assignedToUserIds = vals;
  }

  // --- Date filter ---
  const dueDateFilter = filterModel["dueDate"] as DateFilterModel | undefined;
  if (dueDateFilter?.filterType === "date") {
    if (dueDateFilter.type === "inRange") {
      if (dueDateFilter.dateFrom) result.dueDateAfter = toDateOnly(dueDateFilter.dateFrom);
      if (dueDateFilter.dateTo) result.dueDateBefore = toDateOnly(dueDateFilter.dateTo);
    } else if (dueDateFilter.type === "lessThan" && dueDateFilter.dateFrom) {
      result.dueDateBefore = toDateOnly(dueDateFilter.dateFrom);
    } else if (
      (dueDateFilter.type === "greaterThan" || dueDateFilter.type === "greaterThanOrEqual") &&
      dueDateFilter.dateFrom
    ) {
      result.dueDateAfter = toDateOnly(dueDateFilter.dateFrom);
    }
  }

  return result;
}
